import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, ArrowRight, Check, Users, BookOpen,
  ChevronLeft, ChevronRight, ChevronsRight, AlertCircle, DollarSign, Clock, Target,
  Zap, Sparkles, CheckCircle, Copy, Search,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { OFFERING_TYPE_THEME, getOfferingTypeTheme } from '@/lib/offeringTypeTheme';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { currencySymbol } from '@/lib/currency';
import { TeacherSidebar } from '@/components/teacher/dashboard/TeacherSidebar';
import { SidebarProvider } from '@/components/ui/sidebar';
import { DashboardTopNav } from '@/components/layout/DashboardTopNav';
import { RichTextEditor } from '@/components/common/profile-setup/RichTextEditor';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useTeacherProfile } from '@/contexts/TeacherProfileContext';
import { useAuth } from '@/hooks/use-auth-compat';
import { fetchSubjects } from '@/services/AdminFetchServices';
import { useExamCatalog } from '@/hooks/useExamCatalog';
import { useTeachingModes, useGradeBands, useCurricula, useAcceptedLevels } from '@/hooks/useValueCatalog';
import { useTeachingModeDefault } from '@/hooks/useTeachingModeDefault';
import { FieldHint } from '@/components/help/FieldHint';
import offeringWizardHelp from '@/content/help/offering-wizard-help.json';
import { fullTemplates, type TemplateContext } from '@/content/offeringTemplates';
import { useMyOfferings } from '@/hooks/useMyOfferings';
import { checkOfferingDuplicates, type DuplicateConflicts } from '@/lib/offeringDuplicateCheck';
import { findLeftoverPlaceholders } from '@/lib/offeringPlaceholders';
import { migrateCrashCourseMilestones } from '@/lib/offeringMigration';
import {
  createOffering, getOfferingById, updateOffering,
  publishOfferingById, upsertPricingPlan,
} from '@/services/OfferingServices';
import { AvailabilitySlotsBuilder } from '@/components/teacher/offering/AvailabilitySlotsBuilder';
import { MilestonesBuilder } from '@/components/teacher/offering/MilestonesBuilder';
import { PriceListEditor } from '@/components/teacher/offering/PriceListEditor';
import { PublishRestrictionNotice } from '@/components/teacher/offering/PublishRestrictionNotice';
import { isReleaseFlagOn } from '@/config/featureFlags';
import { useTeacherEntitlements } from '@/hooks/use-teacher-entitlements';
import {
  computeProactivePublishBlock, parsePublishBlockReason, type OfferingPublishBlockReason,
} from '@/lib/publishRestrictions';
import type {
  OfferingType, AvailabilitySlot, CurriculumMilestone,
  CreateOfferingRequest, OfferingResponse, PricingPlanRequest,
} from '@/types/offering';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Strip HTML tags and decode entities (e.g. &nbsp;) to plain text. */
const stripHtml = (html: string): string => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent ?? div.innerText ?? '';
};


// ─── Constants ───────────────────────────────────────────────────────────────

const DURATION_OPTIONS = [30, 45, 60, 75, 90, 120];

// Accent colors + icons for the quick-start template cards, keyed by template label.
const TEMPLATE_ACCENTS: Record<string, { icon: React.ElementType; iconBg: string; iconText: string; title: string }> = {
  'Exam prep':     { icon: Target,   iconBg: 'bg-rose-100',    iconText: 'text-rose-600',    title: 'text-rose-700' },
  'Build basics':  { icon: BookOpen, iconBg: 'bg-emerald-100', iconText: 'text-emerald-600', title: 'text-emerald-700' },
  'Crash course':  { icon: Zap,      iconBg: 'bg-amber-100',   iconText: 'text-amber-600',   title: 'text-amber-700' },
  'Group classes': { icon: Users,    iconBg: 'bg-sky-100',     iconText: 'text-sky-600',     title: 'text-sky-700' },
  default:         { icon: Sparkles, iconBg: 'bg-violet-100',  iconText: 'text-violet-600',  title: 'text-violet-700' },
};

const STEP_LABELS = [
  'Audience', 'Details', 'Outcomes',
  'Schedule', 'Curriculum', 'Pricing',
];

const WIZARD_STEPS = [
  { id: 'audience',    label: 'Audience',    icon: Target,      optional: false },
  { id: 'details',     label: 'Details',     icon: BookOpen,    optional: false },
  { id: 'outcomes',    label: 'Outcomes',    icon: CheckCircle, optional: false },
  { id: 'schedule',    label: 'Schedule',    icon: Zap,         optional: false },
  { id: 'curriculum',  label: 'Curriculum',  icon: BookOpen,    optional: true  },
  { id: 'pricing',     label: 'Pricing',     icon: DollarSign,  optional: false },
];

// Offering type options shown in the persistent top picker
const OFFERING_TYPE_OPTIONS = [
  { type: 'ONE_TO_ONE' as const,   label: 'One-to-One',   desc: 'Private sessions tailored to a single student' },
  { type: 'GROUP' as const,        label: 'Group',         desc: 'Batch teaching for multiple students together' },
  { type: 'CRASH_COURSE' as const, label: 'Crash Course',  desc: 'Intensive time-bound program with a fixed cohort' },
];

// ─── Default state ────────────────────────────────────────────────────────────

const defaultFormData = () => ({
  // Step 1
  offeringType: '' as OfferingType | '',
  // Step 2
  title: '',
  description: '',
  category: 'ACADEMIC',
  teachingMode: '',
  // Step 3
  gradeBands: [] as string[],
  curriculumSystems: [] as string[],
  subjectId: '',
  topicIds: [] as string[],
  acceptedLevels: [] as string[],
  targetExamIds: [] as string[],
  // Step 4
  learningOutcomes: '',
  prerequisites: '',
  targetScorePromise: '',
  // Step 5
  availabilitySlots: [] as AvailabilitySlot[],
  trialSessionAvailable: false,
  earlyBirdDeadline: '',
  // Step 6 — ONE_TO_ONE
  oneToOneSessionDurationMinutes: 60 as number | '',
  // Step 6 — GROUP
  groupBatchSizeMin: '' as number | '',
  groupBatchSizeMax: '' as number | '',
  groupSessionsPerWeek: '' as number | '',
  groupSessionDurationMinutes: 60 as number | '',
  groupTotalSessionsPlanned: '' as number | '',
  groupNextBatchStartDate: '',
  groupMilestones: [] as CurriculumMilestone[],
  // Step 6 — CRASH_COURSE
  crashCourseStartDate: '',
  crashCourseEndDate: '',
  crashCourseTotalSessions: '' as number | '',
  crashCourseSessionsPerWeek: '' as number | '',
  crashCourseSessionDurationMinutes: 60 as number | '',
  crashCourseSeatsTotal: '' as number | '',
  crashCourseMinimumEntryLevel: '',
  crashCourseStudyMaterial: false,
  crashCourseMockTestsIncluded: '' as number | '',
  crashCourseDoubtClearingSessionsPerWeek: '' as number | '',
  crashCoursePreviousBatchResults: '',
  crashCourseMilestones: [] as CurriculumMilestone[],
  // Step 7
  pricingPlans: [] as PricingPlanRequest[],
});

type FormData = ReturnType<typeof defaultFormData>;

/** SCRUM-365: keep only the INR plan; with none, one blank INR row (D4). */
function keepInrPlanOnly(plans: PricingPlanRequest[]): PricingPlanRequest[] {
  const inr = plans.filter(p => p.currency === 'INR');
  return inr.length > 0 ? inr : [{ currency: 'INR', regularPrice: '' }];
}

// ─── UI helpers ───────────────────────────────────────────────────────────────


function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none cursor-pointer',
        checked ? 'bg-primary' : 'bg-slate-200',
      )}
    >
      <span className={cn(
        'inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform',
        checked ? 'translate-x-6' : 'translate-x-1',
      )} />
    </button>
  );
}

function FieldGroup({ label, required, hint, help, children }: {
  label: string; required?: boolean; hint?: string; help?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-semibold text-slate-700">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        {hint && <span className="text-slate-400 font-normal ml-1">({hint})</span>}
        {help}
      </Label>
      {children}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-3">{children}</p>
  );
}

const selectCls = 'w-full h-10 rounded-lg border border-transparent bg-muted/60 px-3 text-sm text-foreground focus:outline-none focus:bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors';

// ─── Main component ───────────────────────────────────────────────────────────

export default function CreateOfferingWizard() {
  const { type: typeParam, id } = useParams<{ type?: string; id?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const auth = useAuth();

  const { profileResponse, hasActiveVersion, refreshProfile } = useTeacherProfile();
  const isProfileApproved = hasActiveVersion;
  const teacherName = (profileResponse?.profile?.activeVersion?.data ?? profileResponse?.profile?.draftVersion?.data)?.fullName ?? '';
  const teacherAvatar = (profileResponse?.profile?.activeVersion?.data ?? profileResponse?.profile?.draftVersion?.data)?.profileImage ?? '';

  const token = auth.user?.access_token ?? '';
  const { teachingMode: defaultTeachingMode } = useTeachingModeDefault(token || null);
  const { exams } = useExamCatalog();
  const teachingModes = useTeachingModes();
  const gradeBands = useGradeBands();
  const curricula = useCurricula();
  const acceptedLevels = useAcceptedLevels();

  const [catalogSubjects, setCatalogSubjects] = useState<{ id: string; name: string; topics: { id: string; name: string }[] }[]>([]);
  const normalizedTypeParam = typeParam?.toUpperCase();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // F15 — 409 from POST /publish (OFFERING_TYPE_NOT_ALLOWED | OFFERING_LIMIT_REACHED),
  // recovered from the caught error's message. See src/lib/publishRestrictions.ts.
  const [publishBlock, setPublishBlock] = useState<OfferingPublishBlockReason | null>(null);
  const [existingOffering, setExistingOffering] = useState<OfferingResponse | null>(null);
  // SCRUM-365 (D4/D5): authoring holds only the INR plan, and saves never delete.
  const [formData, setFormData] = useState<FormData>(() => (
    { ...defaultFormData(), pricingPlans: [{ currency: 'INR', regularPrice: '' }] }
  ));
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());
  const [templatePanelOpen, setTemplatePanelOpen] = useState(false);
  // Set on a failed publish; fields with leftover [placeholders] stay highlighted
  // (live-checked, so each highlight clears as the teacher fixes that field).
  const [showPlaceholderErrors, setShowPlaceholderErrors] = useState(false);
  const [dupErrors, setDupErrors] = useState<DuplicateConflicts | null>(null);
  const [clonePopoverOpen, setClonePopoverOpen] = useState(false);
  const [cloneSearch, setCloneSearch] = useState('');

  // F15 — best-effort, proactive publish-restriction check (TEACHER_TIER_BENEFITS).
  // The 409 from POST /publish (captured into `publishBlock`) remains the source
  // of truth; this only lets the CTA warn/disable itself before an attempt.
  const tierBenefitsOn = isReleaseFlagOn('TEACHER_TIER_BENEFITS');
  const entitlements = useTeacherEntitlements();
  const proactivePublishBlock = tierBenefitsOn
    ? computeProactivePublishBlock(formData.offeringType, entitlements.data)
    : null;
  const effectivePublishBlock = publishBlock ?? proactivePublishBlock;

  // Subjects filtered to teacher's profile
  const subjects = useMemo(() => {
    const versionData = profileResponse?.profile?.activeVersion?.data
      ?? profileResponse?.profile?.draftVersion?.data;
    if (!versionData?.subjects) return catalogSubjects;
    const profileSubjectIds = new Set(versionData.subjects.map((s: any) => s.subjectId));
    const filtered = catalogSubjects.filter(s => profileSubjectIds.has(s.id));
    return filtered.length > 0 ? filtered : catalogSubjects;
  }, [profileResponse, catalogSubjects]);

  const topicsForSubject = useMemo(() => {
    if (!formData.subjectId) return [];
    return catalogSubjects.find(s => s.id === formData.subjectId)?.topics ?? [];
  }, [formData.subjectId, catalogSubjects]);

  const teacherSubjects = subjects.map(s => s.name);

  // Context for the fill-in-the-blank starter templates: uses whatever the
  // teacher has already selected; missing values become [placeholders].
  const templateCtx: TemplateContext = useMemo(() => {
    const versionData = profileResponse?.profile?.activeVersion?.data
      ?? profileResponse?.profile?.draftVersion?.data;
    // catalogSubjects may not have loaded yet (async fetch); fall back to the
    // subjectName stored on the profile subject so templates resolve immediately.
    const catalogName = catalogSubjects.find(s => s.id === formData.subjectId)?.name;
    const profileName = (versionData?.subjects as any[] | undefined)
      ?.find((s: any) => s.subjectId === formData.subjectId)?.subjectName;
    return {
      subjectName: catalogName ?? profileName,
      gradeLabel: formData.gradeBands[0] ? gradeBands.labelOf(formData.gradeBands[0]) : undefined,
      curriculumLabel: formData.curriculumSystems[0] ? curricula.labelOf(formData.curriculumSystems[0]) : undefined,
      offeringType: formData.offeringType || undefined,
    };
  }, [catalogSubjects, profileResponse, formData.subjectId, formData.gradeBands, formData.curriculumSystems, formData.offeringType, gradeBands, curricula]);

  // Load catalog subjects
  useEffect(() => {
    fetchSubjects()
      .then(data => { if (data) setCatalogSubjects(Array.isArray(data) ? data : []); })
      .catch(() => { /* ignore */ });
    // Always fetch latest profile on page mount (catches admin approval/rejection)
    refreshProfile(true);
  }, []);

  // Pre-fill teaching mode from profile default
  useEffect(() => {
    if (defaultTeachingMode && !formData.teachingMode) {
      setFormData(d => ({ ...d, teachingMode: defaultTeachingMode }));
    }
  }, [defaultTeachingMode]);

  // Pre-select type from URL param
  useEffect(() => {
    if (!normalizedTypeParam || !['ONE_TO_ONE', 'GROUP', 'CRASH_COURSE'].includes(normalizedTypeParam)) return;
    setFormData(d => ({ ...d, offeringType: normalizedTypeParam as OfferingType }));
  }, [normalizedTypeParam]);

  // Load existing offering for edit mode
  useEffect(() => {
    if (!id) return;
    const tok = auth.user?.access_token || sessionStorage.getItem('access_token');
    if (!tok) return;
    getOfferingById(id, tok).then(o => {
      if (!o) return;
      setExistingOffering(o);
      setFormData(d => ({
        ...d,
        offeringType: o.offeringType,
        title: o.title ?? '',
        description: o.description ?? '',
        category: o.category ?? 'ACADEMIC',
        teachingMode: o.teachingMode ?? '',
        gradeBands: o.gradeBands ?? [],
        curriculumSystems: o.curriculumSystems ?? [],
        subjectId: o.subjectId ?? '',
        topicIds: o.topicIds ?? [],
        acceptedLevels: o.acceptedLevels ?? [],
        targetExamIds: o.targetExamIds ?? [],
        learningOutcomes: o.learningOutcomes ?? '',
        prerequisites: o.prerequisites ?? '',
        targetScorePromise: o.targetScorePromise ?? '',
        availabilitySlots: o.availabilitySlots ?? [],
        trialSessionAvailable: o.trialSessionAvailable ?? false,
        earlyBirdDeadline: o.earlyBirdDeadline ?? '',
        // ONE_TO_ONE
        oneToOneSessionDurationMinutes: o.oneToOne?.sessionDurationMinutes ?? '',
        // GROUP
        groupBatchSizeMin: o.group?.batchSizeMin ?? '',
        groupBatchSizeMax: o.group?.batchSizeMax ?? '',
        groupSessionsPerWeek: o.group?.sessionsPerWeek ?? '',
        groupSessionDurationMinutes: o.group?.sessionDurationMinutes ?? '',
        groupTotalSessionsPlanned: o.group?.totalSessionsPlanned ?? '',
        groupNextBatchStartDate: o.group?.nextBatchStartDate ?? '',
        groupMilestones: o.group?.milestones ?? o.group?.curriculumMilestones ?? [],
        // CRASH_COURSE
        crashCourseStartDate: o.crashCourse?.courseStartDate ?? '',
        crashCourseEndDate: o.crashCourse?.courseEndDate ?? '',
        crashCourseTotalSessions: o.crashCourse?.totalSessions ?? '',
        crashCourseSessionsPerWeek: o.crashCourse?.sessionsPerWeek ?? '',
        crashCourseSessionDurationMinutes: o.crashCourse?.sessionDurationMinutes ?? '',
        crashCourseSeatsTotal: o.crashCourse?.seatsTotal ?? '',
        crashCourseMinimumEntryLevel: o.crashCourse?.minimumEntryLevel ?? '',
        crashCourseStudyMaterial: o.crashCourse?.studyMaterialIncluded ?? false,
        crashCourseMockTestsIncluded: o.crashCourse?.mockTestsIncluded ?? '',
        crashCourseDoubtClearingSessionsPerWeek: o.crashCourse?.doubtClearingSessionsPerWeek ?? '',
        crashCoursePreviousBatchResults: o.crashCourse?.previousBatchResults ?? '',
        crashCourseMilestones: migrateCrashCourseMilestones(o.crashCourse),
        pricingPlans: keepInrPlanOnly(o.pricingPlans?.map(p => ({
          currency: p.currency as any,
          regularPrice: p.regularPrice,
          earlyBirdPrice: p.earlyBirdPrice ?? '',
          trialSessionPrice: p.trialSessionPrice ?? '',
        })) ?? []),
      }));
      setStep(0); // start at targeting step in edit mode
    }).catch(console.error);
  }, [id]);

  // Prefill form when cloning an existing offering (navigate with state.cloneFrom)
  const location = useLocation();
  const hasClonePrefill = Boolean((location.state as any)?.cloneFrom || (typeof window !== 'undefined' && sessionStorage.getItem('cloneFromOffering')));
  const isEditingMode = Boolean(id || (location.state as any)?.isEditing || hasClonePrefill);
  // Fetch existing offerings for the clone-from section (only when creating, not editing)
  const myOfferings = useMyOfferings(!isEditingMode);
  useEffect(() => {
    let clone = (location.state as any)?.cloneFrom;
    if (!clone && typeof window !== 'undefined') {
      try {
        const stored = sessionStorage.getItem('cloneFromOffering');
        if (stored) {
          clone = JSON.parse(stored);
          sessionStorage.removeItem('cloneFromOffering');
        }
      } catch (e) { /* ignore */ }
    }
    if (!clone || id) return; // only when creating (no id)
    setFormData(d => ({
      ...d,
      offeringType: clone.offeringType ?? clone.type ?? d.offeringType,
      title: clone.title ?? clone.commercialName ?? d.title,
      description: clone.description ?? clone.longDescription ?? d.description,
      category: clone.category ?? d.category,
      teachingMode: clone.teachingMode ?? d.teachingMode,
      gradeBands: clone.gradeBands ?? d.gradeBands,
      curriculumSystems: clone.curriculumSystems ?? d.curriculumSystems,
      subjectId: clone.subjectId ?? d.subjectId,
      topicIds: clone.topicIds ?? d.topicIds,
      acceptedLevels: clone.acceptedLevels ?? d.acceptedLevels,
      targetExamIds: clone.targetExamIds ?? d.targetExamIds,
      learningOutcomes: clone.learningOutcomes ?? d.learningOutcomes,
      prerequisites: clone.prerequisites ?? d.prerequisites,
      targetScorePromise: clone.targetScorePromise ?? d.targetScorePromise,
      availabilitySlots: clone.availabilitySlots ?? d.availabilitySlots,
      trialSessionAvailable: clone.trialSessionAvailable ?? d.trialSessionAvailable,
      earlyBirdDeadline: clone.earlyBirdDeadline ?? d.earlyBirdDeadline,
      oneToOneSessionDurationMinutes: clone.oneToOne?.sessionDurationMinutes ?? clone.oneToOne?.durationMinutes ?? d.oneToOneSessionDurationMinutes,
      groupBatchSizeMin: clone.group?.batchSizeMin ?? d.groupBatchSizeMin,
      groupBatchSizeMax: clone.group?.batchSizeMax ?? d.groupBatchSizeMax,
      groupSessionsPerWeek: clone.group?.sessionsPerWeek ?? d.groupSessionsPerWeek,
      groupSessionDurationMinutes: clone.group?.sessionDurationMinutes ?? d.groupSessionDurationMinutes,
      groupTotalSessionsPlanned: clone.group?.totalSessionsPlanned ?? d.groupTotalSessionsPlanned,
      groupNextBatchStartDate: clone.group?.nextBatchStartDate ?? d.groupNextBatchStartDate,
      groupMilestones: clone.group?.milestones ?? clone.group?.curriculumMilestones ?? d.groupMilestones,
      crashCourseStartDate: clone.crashCourse?.courseStartDate ?? d.crashCourseStartDate,
      crashCourseEndDate: clone.crashCourse?.courseEndDate ?? d.crashCourseEndDate,
      crashCourseTotalSessions: clone.crashCourse?.totalSessions ?? d.crashCourseTotalSessions,
      crashCourseSessionsPerWeek: clone.crashCourse?.sessionsPerWeek ?? d.crashCourseSessionsPerWeek,
      crashCourseSessionDurationMinutes: clone.crashCourse?.sessionDurationMinutes ?? d.crashCourseSessionDurationMinutes,
      crashCourseSeatsTotal: clone.crashCourse?.seatsTotal ?? d.crashCourseSeatsTotal,
      crashCourseMinimumEntryLevel: clone.crashCourse?.minimumEntryLevel ?? d.crashCourseMinimumEntryLevel,
      crashCourseStudyMaterial: clone.crashCourse?.studyMaterialIncluded ?? d.crashCourseStudyMaterial,
      crashCourseMockTestsIncluded: clone.crashCourse?.mockTestsIncluded ?? d.crashCourseMockTestsIncluded,
      crashCourseDoubtClearingSessionsPerWeek: clone.crashCourse?.doubtClearingSessionsPerWeek ?? d.crashCourseDoubtClearingSessionsPerWeek,
      crashCoursePreviousBatchResults: clone.crashCourse?.previousBatchResults ?? d.crashCoursePreviousBatchResults,
      crashCourseMilestones: migrateCrashCourseMilestones(clone.crashCourse) ?? d.crashCourseMilestones,
      pricingPlans: keepInrPlanOnly(clone.pricingPlans?.map((p: any) => ({
        currency: p.currency,
        regularPrice: p.regularPrice,
        earlyBirdPrice: p.earlyBirdPrice ?? '',
        trialSessionPrice: p.trialSessionPrice ?? '',
      })) ?? d.pricingPlans),
    }));
    setStep(0);
    // remove cloneFrom from history state to avoid reapplying on navigation
    try { (window.history.replaceState as any)({ ...window.history.state, usr: { ...window.history.state?.usr, cloneFrom: undefined } }, ''); } catch { /* ignore */ }
    // clear sessionStorage fallback so unrelated Create flows aren't prefilled
    try { sessionStorage.removeItem('cloneFromOffering'); } catch { /* ignore */ }
  }, [location, id]);

  // Prefill offeringType from URL param (e.g. /create-offering?type=GROUP)
  useEffect(() => {
    if (normalizedTypeParam && ['ONE_TO_ONE', 'GROUP', 'CRASH_COURSE'].includes(normalizedTypeParam)) {
      update('offeringType', normalizedTypeParam as OfferingType);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedTypeParam]);

  // Prefill targeting from the teacher's profile for brand-new offerings
  // (not edit, not clone). Only fills fields the teacher hasn't touched yet,
  // and only once per wizard mount.
  const didProfilePrefill = useRef(false);
  useEffect(() => {
    if (id || hasClonePrefill || didProfilePrefill.current) return;
    const versionData = profileResponse?.profile?.activeVersion?.data
      ?? profileResponse?.profile?.draftVersion?.data;
    if (!versionData) return;
    didProfilePrefill.current = true;
    setFormData(d => ({
      ...d,
      gradeBands: d.gradeBands.length === 0 && versionData.teachingAudience?.length
        ? [...versionData.teachingAudience] : d.gradeBands,
      curriculumSystems: d.curriculumSystems.length === 0 && versionData.curriculumSystems?.length
        ? [...versionData.curriculumSystems] : d.curriculumSystems,
      // Auto-pick the subject only when the profile has exactly one — with
      // multiple subjects the teacher must choose per offering anyway.
      subjectId: !d.subjectId && versionData.subjects?.length === 1
        ? versionData.subjects[0].subjectId : d.subjectId,
    }));
  }, [id, hasClonePrefill, profileResponse]);

  // Clone flow only: surface a Cancel option on the Details step (step 1) when
  // the cloned draft already looks like a duplicate of an existing offering, so
  // the teacher can bail out before filling in the rest of the wizard.
  const [showCloneCancelConfirm, setShowCloneCancelConfirm] = useState(false);
  const cloneDuplicateDetected = useMemo(() => {
    if (!hasClonePrefill || id) return false;
    if (formData.offeringType !== 'ONE_TO_ONE' && formData.offeringType !== 'GROUP' && formData.offeringType !== 'CRASH_COURSE') return false;
    if (!myOfferings.data) return false;
    return checkOfferingDuplicates({
      title: formData.title,
      subjectId: formData.subjectId,
      gradeBands: formData.gradeBands,
      curriculumSystems: formData.curriculumSystems,
      offeringType: formData.offeringType,
      existingOfferings: myOfferings.data,
      nextBatchStartDate: formData.offeringType === 'GROUP' ? formData.groupNextBatchStartDate : undefined,
      courseStartDate: formData.offeringType === 'CRASH_COURSE' ? formData.crashCourseStartDate : undefined,
      courseEndDate: formData.offeringType === 'CRASH_COURSE' ? formData.crashCourseEndDate : undefined,
    }).hasAny;
  }, [
    hasClonePrefill, id, formData.offeringType, formData.title, formData.subjectId,
    formData.gradeBands, formData.curriculumSystems, formData.groupNextBatchStartDate,
    formData.crashCourseStartDate, formData.crashCourseEndDate, myOfferings.data,
  ]);
  const showCloneCancelButton = step === 1 && cloneDuplicateDetected;

  const update = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(d => ({ ...d, [field]: value }));
    setFieldErrors(prev => { const next = new Set(prev); next.delete(field as string); return next; });
    if (
      field === 'title' ||
      field === 'subjectId' ||
      field === 'groupNextBatchStartDate' ||
      field === 'crashCourseStartDate' ||
      field === 'crashCourseEndDate'
    ) setDupErrors(null);
  };

  const focusField = (id: string) => {
    requestAnimationFrame(() => document.getElementById(id)?.focus());
  };

  // Pure per-step error computation — used by validateStep (which also sets
  // the error state) and by the step indicator to show real completion.
  const computeStepErrors = (s: number): Set<string> => {
    const errors = new Set<string>();
    if (s === 0) {
      if (!formData.subjectId) errors.add('subjectId');
    } else if (s === 1) {
      if (!formData.title.trim()) errors.add('title');
      else if (formData.title.length > 200 || formData.title.trim().split(/\s+/).length > 15) errors.add('title');
      if (!formData.teachingMode) errors.add('teachingMode');
      const descText = stripHtml(formData.description).trim();
      if (!descText) errors.add('description');
    } else if (s === 2) {
      const outcomeText = stripHtml(formData.learningOutcomes).trim();
      if (!outcomeText) errors.add('learningOutcomes');
    } else if (s === 3) {
      // Availability slots: flag duplicates
      const slots = formData.availabilitySlots ?? [];
      const seen = new Set<string>();
      for (const sl of slots) {
        const key = `${sl.dayOfWeek}|${sl.timeFrom}|${sl.timeTo}`;
        if (seen.has(key)) { errors.add('availabilitySlots'); break; }
        seen.add(key);
      }
      // Type-specific setup fields
      if (formData.offeringType === 'ONE_TO_ONE') {
        if (!formData.oneToOneSessionDurationMinutes) errors.add('oneToOneSessionDurationMinutes');
      } else if (formData.offeringType === 'GROUP') {
        if (!formData.groupBatchSizeMin) errors.add('groupBatchSizeMin');
        if (!formData.groupBatchSizeMax) errors.add('groupBatchSizeMax');
        if (!formData.groupSessionsPerWeek) errors.add('groupSessionsPerWeek');
        if (!formData.groupSessionDurationMinutes) errors.add('groupSessionDurationMinutes');
        if (!formData.groupTotalSessionsPlanned) errors.add('groupTotalSessionsPlanned');
        if (!formData.groupNextBatchStartDate) errors.add('groupNextBatchStartDate');
      } else if (formData.offeringType === 'CRASH_COURSE') {
        if (!formData.crashCourseStartDate) errors.add('crashCourseStartDate');
        if (!formData.crashCourseEndDate) errors.add('crashCourseEndDate');
        if (!formData.crashCourseTotalSessions) errors.add('crashCourseTotalSessions');
        if (!formData.crashCourseSessionsPerWeek) errors.add('crashCourseSessionsPerWeek');
        if (!formData.crashCourseSessionDurationMinutes) errors.add('crashCourseSessionDurationMinutes');
        if (!formData.crashCourseSeatsTotal) errors.add('crashCourseSeatsTotal');
        if (!formData.crashCourseMinimumEntryLevel) errors.add('crashCourseMinimumEntryLevel');
      }
    } else if (s === 4) {
      // Curriculum step: milestones required for GROUP and CRASH_COURSE
      if (formData.offeringType === 'GROUP') {
        if (formData.groupMilestones.length === 0) errors.add('groupMilestones');
        formData.groupMilestones.forEach((m, i) => {
          if (!(m.title?.trim())) errors.add(`groupMilestone_${i}_title`);
        });
      } else if (formData.offeringType === 'CRASH_COURSE') {
        if (!formData.crashCourseMilestones || formData.crashCourseMilestones.length === 0) errors.add('crashCourseMilestones');
        (formData.crashCourseMilestones ?? []).forEach((m, i) => {
          if (!(m.title?.trim())) errors.add(`crashMilestone_${i}_title`);
        });
      }
      // ONE_TO_ONE: no curriculum required — step is always complete
    } else if (s === 5) {
      const filledPlans = formData.pricingPlans.filter(p => p.currency && p.regularPrice !== '');
      if (filledPlans.length === 0) errors.add('pricingPlans');
    }
    return errors;
  };

  const validateStep = (s: number): boolean => {
    const errors = computeStepErrors(s);
    setFieldErrors(errors);
    return errors.size === 0;
  };

  const toggleChip = (field: keyof FormData, value: string) => {
    const arr = formData[field] as string[];
    update(field, (arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]) as FormData[typeof field]);
    if (field === 'gradeBands' || field === 'curriculumSystems') setDupErrors(null);
  };

  // ─── Build API request ──────────────────────────────────────────────────────

  const buildRequest = (): CreateOfferingRequest => {
    const base: CreateOfferingRequest = {
      offeringType: formData.offeringType as OfferingType,
      title: formData.title,
      description: formData.description,
      category: formData.category,
      teachingMode: formData.teachingMode,
      gradeBands: formData.gradeBands,
      curriculumSystems: formData.curriculumSystems,
      subjectId: formData.subjectId,
      topicIds: formData.topicIds,
      acceptedLevels: formData.offeringType !== 'CRASH_COURSE' ? formData.acceptedLevels : undefined,
      targetExamIds: formData.targetExamIds.length > 0 ? formData.targetExamIds : undefined,
      learningOutcomes: formData.learningOutcomes || undefined,
      prerequisites: formData.prerequisites || undefined,
      targetScorePromise: formData.targetScorePromise || undefined,
      trialSessionAvailable: formData.trialSessionAvailable,
      earlyBirdDeadline: formData.earlyBirdDeadline || undefined,
      availabilitySlots: formData.availabilitySlots,
    };

    if (formData.offeringType === 'ONE_TO_ONE') {
      base.oneToOne = { sessionDurationMinutes: Number(formData.oneToOneSessionDurationMinutes) };
    } else if (formData.offeringType === 'GROUP') {
      base.group = {
        batchSizeMin: Number(formData.groupBatchSizeMin),
        batchSizeMax: Number(formData.groupBatchSizeMax),
        sessionsPerWeek: Number(formData.groupSessionsPerWeek),
        sessionDurationMinutes: Number(formData.groupSessionDurationMinutes),
        totalSessionsPlanned: Number(formData.groupTotalSessionsPlanned),
        nextBatchStartDate: formData.groupNextBatchStartDate,
        // Map CurriculumMilestone → MilestoneRequest (backend DTO shape)
        curriculumMilestones: formData.groupMilestones.map(m => ({
          weekNumber: m.weekNumber,
          title: m.title,
          topicIds: m.topicIds,
          hasAssessment: m.assessmentType !== 'NONE',
        })),
      };
    } else if (formData.offeringType === 'CRASH_COURSE') {
      base.crashCourse = {
        courseStartDate: formData.crashCourseStartDate,
        courseEndDate: formData.crashCourseEndDate,
        totalSessions: Number(formData.crashCourseTotalSessions),
        sessionsPerWeek: Number(formData.crashCourseSessionsPerWeek),
        sessionDurationMinutes: Number(formData.crashCourseSessionDurationMinutes),
        seatsTotal: Number(formData.crashCourseSeatsTotal),
        minimumEntryLevel: formData.crashCourseMinimumEntryLevel,
        studyMaterialIncluded: formData.crashCourseStudyMaterial,
        mockTestsIncluded: Number(formData.crashCourseMockTestsIncluded) || 0,
        doubtClearingSessionsPerWeek: Number(formData.crashCourseDoubtClearingSessionsPerWeek) || 0,
        previousBatchResults: formData.crashCoursePreviousBatchResults || null,
        // Map CurriculumMilestone → WeekPlanRequest (backend DTO shape)
        weekPlan: formData.crashCourseMilestones.map(m => ({
          weekNumber: m.weekNumber,
          theme: m.title,
          topicIds: m.topicIds,
          mockTest: m.assessmentType === 'MOCK_TEST',
          revisionSession: false,
        })),
      };
    }

    return base;
  };

  // ─── Save draft ─────────────────────────────────────────────────────────────

  const handleSaveDraft = async () => {
    // Apply only the current step's own business rules (matches the Next
    // button's per-step validation) — not the whole wizard's, since later
    // steps haven't been visited yet. Title itself is separately enforced
    // via the button's disabled state regardless of step.
    if (!validateStep(step)) return;
    const tok = await auth.getToken();
    if (!tok) { toast.error('Authentication required'); return; }
    setSaving(true);
    try {
      const req = buildRequest();
      let saved: OfferingResponse;
      if (id && existingOffering) {
        saved = await updateOffering(id, req, tok);
      } else {
        saved = await createOffering(req, tok);
        void queryClient.invalidateQueries({ queryKey: ['teacherOfferings'] });
      }
      await syncPricingPlans(saved.id, formData.pricingPlans, tok);
      toast.success(id ? 'Offering updated' : 'Draft saved');
      navigate(`/teacher-offering/${saved.id}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to save offering');
    } finally {
      setSaving(false);
    }
  };

  // ─── Publish ────────────────────────────────────────────────────────────────

  const handlePublish = async () => {
    // Steps can now be skipped via the step indicator, so validate the whole
    // wizard here and jump to the first incomplete step.
    for (let s = 0; s <= 5; s++) {
      if (!validateStep(s)) {
        setStep(s);
        toast.error(`Please complete the "${WIZARD_STEPS[s].label}" step before publishing`);
        return;
      }
    }
    // Warn (not block) if optional-in-quick-flow steps are incomplete
    const incompleteOptional = [2, 3, 4].filter(s => computeStepErrors(s).size > 0);
    if (incompleteOptional.length > 0) {
      const labels = incompleteOptional.map(s => WIZARD_STEPS[s].label).join(', ');
      toast.info(`Tip: Adding ${labels} will help attract more students. You can edit these after publishing.`, { duration: 6000 });
    }
    const leftover = findLeftoverPlaceholders(formData);
    if (leftover.length > 0) {
      setShowPlaceholderErrors(true);
      // Title/Description are on step 1; Learning outcomes/Prerequisites on step 2.
      setStep(leftover.includes('Title') || leftover.includes('Description') ? 1 : 2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const tok = await auth.getToken();
    if (!tok) { toast.error('Authentication required'); return; }
    if (formData.offeringType === 'ONE_TO_ONE' || formData.offeringType === 'GROUP' || formData.offeringType === 'CRASH_COURSE') {
      const result = await myOfferings.refetch();
      if (result.isError || result.status === 'error' || result.data === undefined) {
        toast.error('Could not verify your existing offerings. Please try again.');
        return;
      }
      const conflicts = checkOfferingDuplicates({
        title: formData.title,
        subjectId: formData.subjectId,
        gradeBands: formData.gradeBands,
        curriculumSystems: formData.curriculumSystems,
        offeringType: formData.offeringType,
        editingOfferingId: existingOffering?.id,
        existingOfferings: result.data,
        nextBatchStartDate: formData.offeringType === 'GROUP' ? formData.groupNextBatchStartDate : undefined,
        courseStartDate: formData.offeringType === 'CRASH_COURSE' ? formData.crashCourseStartDate : undefined,
        courseEndDate: formData.offeringType === 'CRASH_COURSE' ? formData.crashCourseEndDate : undefined,
      });
      if (conflicts.hasAny) {
        setDupErrors(conflicts);
        if (conflicts.title) {
          setStep(1);
          focusField('offering-title');
        } else if (conflicts.gradeBands.length > 0 || conflicts.curriculumSystems.length > 0) {
          setStep(2);
          focusField(conflicts.gradeBands.length > 0 ? 'grade-bands-group' : 'curriculum-group');
        } else if (conflicts.nextBatchDate.length > 0) {
          setStep(5);
          focusField('group-next-batch-date');
        } else if (conflicts.courseDates.length > 0) {
          setStep(5);
          focusField('crash-course-start-date');
        }
        return;
      }
    }
    setPublishing(true);
    setPublishBlock(null);
    try {
      const req = buildRequest();
      let saved: OfferingResponse;
      if (id && existingOffering) {
        saved = await updateOffering(id, req, tok);
      } else {
        saved = await createOffering(req, tok);
        void queryClient.invalidateQueries({ queryKey: ['teacherOfferings'] });
      }
      await syncPricingPlans(saved.id, formData.pricingPlans, tok);
      await publishOfferingById(saved.id, tok);
      toast.success('Offering published!');
      navigate(`/teacher-offering/${saved.id}`);
    } catch (err: any) {
      // F15 — a 409 here means the teacher's subscription tier blocks this
      // publish (wrong offering type, or the plan's publish limit is reached).
      // Surface the specific restriction inline on the review step rather than
      // just a generic toast — the offering itself was already saved above,
      // so nothing is lost and the teacher can act (upgrade) and retry.
      const blocked = parsePublishBlockReason(err);
      if (blocked) {
        setPublishBlock(blocked);
        setStep(5);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        toast.error('Publishing is blocked by your current subscription plan', {
          description: 'See the notice above the Publish button.',
        });
      } else {
        toast.error(err.message || 'Failed to publish offering');
      }
    } finally {
      setPublishing(false);
    }
  };

  // ─── Sync pricing plans ───────────────────────────────────────────────────────

  async function syncPricingPlans(
    offeringId: string,
    next: PricingPlanRequest[],
    tok: string,
  ) {
    const nextValid = next.filter(p => p.currency && p.regularPrice !== '');
    // Never delete (SCRUM-365, D4): non-INR plans are simply not shown, so their absence from
    // the form is not a removal.
    // Upsert all valid plans
    for (const plan of nextValid) {
      await upsertPricingPlan(offeringId, plan.currency as string, {
        regularPrice: Number(plan.regularPrice),
        earlyBirdPrice: plan.earlyBirdPrice !== undefined && plan.earlyBirdPrice !== '' ? Number(plan.earlyBirdPrice) : null,
        trialSessionPrice: plan.trialSessionPrice !== undefined && plan.trialSessionPrice !== '' ? Number(plan.trialSessionPrice) : null,
      }, tok).catch(() => { /* ignore */ });
    }
  }

  // ─── Steps renderers ─────────────────────────────────────────────────────────

  // Clone-from-existing section — shown in the type picker area when the teacher
  // hasn't chosen a type yet and has prior offerings to copy from.
  const applyClone = (o: NonNullable<typeof myOfferings.data>[number]) => {
    setFormData(d => ({
      ...d,
      offeringType: o.offeringType as OfferingType,
      title: `${o.title ?? ''} (copy)`,
      description: o.description ?? '',
      category: o.category ?? 'ACADEMIC',
      teachingMode: o.teachingMode ?? '',
      gradeBands: o.gradeBands ?? [],
      curriculumSystems: o.curriculumSystems ?? [],
      subjectId: o.subjectId ?? '',
      topicIds: o.topicIds ?? [],
      acceptedLevels: o.acceptedLevels ?? [],
      targetExamIds: o.targetExamIds ?? [],
      learningOutcomes: o.learningOutcomes ?? '',
      prerequisites: o.prerequisites ?? '',
      targetScorePromise: o.targetScorePromise ?? '',
      availabilitySlots: o.availabilitySlots ?? [],
      trialSessionAvailable: o.trialSessionAvailable ?? false,
      oneToOneSessionDurationMinutes: o.oneToOne?.sessionDurationMinutes ?? 60,
      groupBatchSizeMin: o.group?.batchSizeMin ?? '',
      groupBatchSizeMax: o.group?.batchSizeMax ?? '',
      groupSessionsPerWeek: o.group?.sessionsPerWeek ?? '',
      groupSessionDurationMinutes: o.group?.sessionDurationMinutes ?? 60,
      groupTotalSessionsPlanned: o.group?.totalSessionsPlanned ?? '',
      groupMilestones: o.group?.milestones ?? [],
      crashCourseSessionDurationMinutes: o.crashCourse?.sessionDurationMinutes ?? 60,
      crashCourseMilestones: migrateCrashCourseMilestones(o.crashCourse),
      pricingPlans: keepInrPlanOnly((o.pricingPlans ?? []).map((p: any) => ({
        currency: p.currency,
        regularPrice: p.regularPrice,
        earlyBirdPrice: p.earlyBirdPrice ?? '',
        trialSessionPrice: p.trialSessionPrice ?? '',
      }))),
    }));
    setClonePopoverOpen(false);
    setCloneSearch('');
    toast.success(`Copied from "${o.title}" — review and adjust before publishing`);
    setStep(0);
  };

  const renderCloneSection = () => {
    if (isEditingMode || !myOfferings.data || myOfferings.data.length === 0) return null;
    const q = cloneSearch.trim().toLowerCase();
    const filtered = q
      ? myOfferings.data.filter(o => (o.title ?? '').toLowerCase().includes(q))
      : myOfferings.data;
    return (
      <div className="border-t border-slate-100 pt-3 mt-1">
        <Popover open={clonePopoverOpen} onOpenChange={(open) => { setClonePopoverOpen(open); if (!open) setCloneSearch(''); }}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2 text-xs h-8">
              <Copy className="w-3.5 h-3.5" />
              Copy from existing
              <span className="ml-0.5 text-xs text-slate-400 font-normal">({myOfferings.data.length})</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            {/* Search */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
              <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <input
                autoFocus
                value={cloneSearch}
                onChange={e => setCloneSearch(e.target.value)}
                placeholder="Search offerings…"
                className="flex-1 text-sm bg-transparent outline-none placeholder:text-slate-400"
              />
            </div>
            {/* List */}
            <div className="max-h-72 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-6">No offerings match "{cloneSearch}"</p>
              ) : filtered.map(o => {
                const theme = OFFERING_TYPE_THEME[o.offeringType as OfferingType] ?? OFFERING_TYPE_THEME.ONE_TO_ONE;
                const OIcon = theme.icon;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => applyClone(o)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left cursor-pointer"
                  >
                    <span className={cn('w-7 h-7 rounded-lg flex items-center justify-center shrink-0', theme.soft)}>
                      <OIcon className={cn('w-3.5 h-3.5', theme.accent)} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{o.title ?? 'Untitled'}</p>
                      <p className="text-xs text-slate-400">{theme.typeLabel}</p>
                    </div>
                    <Copy className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  };

  // One-click quick start: fills title, description, and outcomes together.
  // Auto-shown while all three fields are empty; can be re-opened manually
  // later ("Start from a template instead") — then Undo protects existing text.
  // Live list of fields that still contain [placeholders].
  // Always computed so yellow highlights in the title and the "Next" warning work
  // as soon as a template is applied, without waiting for a failed publish.
  const placeholderErrorFields = findLeftoverPlaceholders(formData);

  const fieldsEmpty =
    !formData.title.trim() &&
    !stripHtml(formData.description).trim() &&
    !stripHtml(formData.learningOutcomes).trim() &&
    !stripHtml(formData.prerequisites).trim();
  const quickStartVisible = fieldsEmpty || templatePanelOpen;

  const applyTemplate = (label: string) => {
    const t = fullTemplates(templateCtx).find(x => x.label === label);
    if (!t) return;
    const prev = {
      title: formData.title,
      description: formData.description,
      learningOutcomes: formData.learningOutcomes,
      prerequisites: formData.prerequisites,
    };
    setFormData(d => ({ ...d, title: t.title, description: t.descriptionHtml, learningOutcomes: t.outcomesHtml, prerequisites: t.prerequisitesHtml }));
    setFieldErrors(p => {
      const n = new Set(p);
      n.delete('title'); n.delete('description'); n.delete('learningOutcomes');
      return n;
    });
    setTemplatePanelOpen(false);
    toast.success(`"${t.label}" template applied`, {
      action: {
        label: 'Undo',
        onClick: () => setFormData(d => ({ ...d, ...prev })),
      },
    });
  };

  const renderStep1 = () => (
    <div className="space-y-5">
      <p className="text-sm text-slate-500 -mt-1">Give your offering a clear title and describe what you'll cover.</p>

      {!quickStartVisible && (
        <button
          type="button"
          onClick={() => setTemplatePanelOpen(true)}
          className="w-full flex items-center justify-between rounded-xl bg-gradient-to-r from-indigo-50 via-violet-50 to-fuchsia-50 px-4 py-3 shadow-sm hover:shadow-md transition-all cursor-pointer group"
        >
          <span className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-white" />
            </span>
            Start from a template instead
          </span>
          <span className="text-xs text-violet-700 font-semibold group-hover:underline">Show templates</span>
        </button>
      )}

      {quickStartVisible && (
        <div className="rounded-xl bg-gradient-to-br from-indigo-50 via-violet-50 to-fuchsia-50 p-4">
          <p className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-white" />
            </span>
            Quick start with a template
          </p>
          <p className="text-xs text-slate-600 mt-1.5 mb-3">
            {fieldsEmpty
              ? 'Fills the title, description, learning outcomes, and prerequisites for you — then just edit the [placeholders].'
              : 'This will replace your current title, description, learning outcomes, and prerequisites. You can undo right after.'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {fullTemplates(templateCtx).map(t => {
              const accent = TEMPLATE_ACCENTS[t.label] ?? TEMPLATE_ACCENTS.default;
              const AccentIcon = accent.icon;
              return (
                <button
                  key={t.label}
                  type="button"
                  onClick={() => applyTemplate(t.label)}
                  className="text-left rounded-xl bg-white px-3.5 py-3 flex items-start gap-3 transition-all cursor-pointer shadow-sm hover:shadow-md hover:-translate-y-0.5"
                >
                  <span className={cn('w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5', accent.iconBg)}>
                    <AccentIcon className={cn('w-5 h-5', accent.iconText)} />
                  </span>
                  <span className="min-w-0">
                    <p className={cn('text-sm font-semibold', accent.title)}>{t.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{t.description}</p>
                  </span>
                </button>
              );
            })}
          </div>
          {!fieldsEmpty && (
            <button
              type="button"
              onClick={() => setTemplatePanelOpen(false)}
              className="text-xs text-slate-500 hover:text-slate-700 hover:underline mt-3 cursor-pointer"
            >
              Keep my text
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <FieldGroup label="Title" required help={<FieldHint content={offeringWizardHelp} stepId="details" fieldKey="title" />}>
            <Input
              id="offering-title"
              value={formData.title}
              maxLength={200}
              onChange={e => update('title', e.target.value)}
              placeholder="e.g. CBSE Grade 10 Maths — Board Prep"
              className={cn('h-10',
                (dupErrors?.title || fieldErrors.has('title')) && 'border-red-500 focus:ring-red-200',
                !dupErrors?.title && !fieldErrors.has('title') && showPlaceholderErrors && placeholderErrorFields.includes('Title') && 'border-yellow-400 bg-yellow-50 focus:ring-yellow-200',
              )}
              aria-invalid={!!dupErrors?.title}
              aria-describedby={dupErrors?.title ? 'offering-title-error' : undefined}
            />
            {dupErrors?.title ? (
              <p className="text-xs text-red-500 mt-1" id="offering-title-error">{dupErrors.title}</p>
            ) : fieldErrors.has('title') ? (
              <p className="text-xs text-red-500 mt-1">
                {!formData.title.trim()
                  ? 'Title is required'
                  : formData.title.length > 200
                  ? 'Title must not exceed 200 characters'
                  : 'Title must be 15 words or fewer'}
              </p>
            ) : showPlaceholderErrors && placeholderErrorFields.includes('Title') ? (
              <p className="text-xs text-amber-600 mt-1">Replace the [bracketed] parts with your own words</p>
            ) : (
              <div className="flex justify-between mt-1">
                <p className={cn('text-xs', formData.title.trim().split(/\s+/).filter(Boolean).length > 15 ? 'text-red-500' : 'text-slate-400')}>
                  {formData.title.trim() ? `${formData.title.trim().split(/\s+/).filter(Boolean).length} / 15 words` : ''}
                </p>
                <p className={cn('text-xs', formData.title.length > 200 ? 'text-red-500' : 'text-slate-400')}>
                  {formData.title.length} / 200
                </p>
              </div>
            )}
          </FieldGroup>
        </div>
        <FieldGroup label="Teaching Mode" required help={<FieldHint content={offeringWizardHelp} stepId="details" fieldKey="teachingMode" />}>
          <select
            value={formData.teachingMode}
            onChange={e => update('teachingMode', e.target.value)}
            className={cn(selectCls, fieldErrors.has('teachingMode') && 'border-red-500 focus:ring-red-200')}
            disabled={teachingModes.isLoading}
          >
            <option value="">Select mode…</option>
            {teachingModes.entries.map(m => (
              <option key={m.code} value={m.code}>{m.label}</option>
            ))}
          </select>
          {fieldErrors.has('teachingMode') && <p className="text-xs text-red-500 mt-1">Teaching mode is required</p>}
          {teachingModes.isError && <p role="alert" className="text-sm text-destructive mt-1">Couldn't load options. Retrying…</p>}
        </FieldGroup>
      </div>

      <FieldGroup label="Description" required help={<FieldHint content={offeringWizardHelp} stepId="details" fieldKey="description" />}>
        <div className={cn((fieldErrors.has('description') || (showPlaceholderErrors && placeholderErrorFields.includes('Description'))) && 'ring-2 ring-red-500 ring-offset-1 rounded-lg')}>
          <RichTextEditor value={formData.description} onChange={v => update('description', v)} placeholderJump />
        </div>
        {fieldErrors.has('description') ? (
          <p className="text-xs text-red-500 mt-1">Description is required</p>
        ) : showPlaceholderErrors && placeholderErrorFields.includes('Description') ? (
          <p className="text-xs text-amber-600 mt-1">Replace the [bracketed] parts with your own words — use "Jump to next" above</p>
        ) : null}
      </FieldGroup>

    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">

      <p className="text-sm text-slate-500 -mt-1">Define who this offering is for — grade, curriculum, subject, and level.</p>

      <div className="space-y-2" id="grade-bands-group" tabIndex={-1}>
        <SectionLabel>Grade Bands <FieldHint content={offeringWizardHelp} stepId="targeting" fieldKey="gradeBands" /></SectionLabel>
        {gradeBands.isError ? (
          <p role="alert" className="text-sm text-destructive">Couldn't load options. Retrying…</p>
        ) : (
          <SearchableMultiSelect
            options={gradeBands.entries.map(e => ({ value: e.code, label: gradeBands.labelOf(e.code) }))}
            selected={formData.gradeBands}
            onChange={v => { update('gradeBands', v); setDupErrors(null); }}
            placeholder={gradeBands.isLoading ? 'Loading…' : 'Select grade bands…'}
            searchPlaceholder="Search grade bands…"
          />
        )}
        {dupErrors?.gradeBands && dupErrors.gradeBands.length > 0 && (
          <div role="alert" id="grade-bands-group-error" className="mt-1 space-y-0.5">
            {dupErrors.gradeBands.map((msg) => (
              <p key={msg} className="text-xs text-red-500">{msg}</p>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2" id="curriculum-group" tabIndex={-1}>
        <SectionLabel>Curriculum Systems <FieldHint content={offeringWizardHelp} stepId="targeting" fieldKey="curriculumSystems" /></SectionLabel>
        {curricula.isError ? (
          <p role="alert" className="text-sm text-destructive">Couldn't load options. Retrying…</p>
        ) : (
          <SearchableMultiSelect
            options={curricula.entries.map(e => ({ value: e.code, label: curricula.labelOf(e.code) }))}
            selected={formData.curriculumSystems}
            onChange={v => { update('curriculumSystems', v); setDupErrors(null); }}
            placeholder={curricula.isLoading ? 'Loading…' : 'Select curriculum systems…'}
            searchPlaceholder="Search curriculum systems…"
          />
        )}
        {dupErrors?.curriculumSystems && dupErrors.curriculumSystems.length > 0 && (
          <div role="alert" id="curriculum-group-error" className="mt-1 space-y-0.5">
            {dupErrors.curriculumSystems.map((msg) => (
              <p key={msg} className="text-xs text-red-500">{msg}</p>
            ))}
          </div>
        )}
      </div>

      {/* Subject gets its own row: catalog names like "Mathematics - Arithmetic - Elementary"
          don't fit a third of the card on a laptop next to the listing preview. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
        <FieldGroup label="Subject" required help={<FieldHint content={offeringWizardHelp} stepId="targeting" fieldKey="subjectId" />}>
          <div className={cn(fieldErrors.has('subjectId') && 'ring-2 ring-red-500 ring-offset-1 rounded-lg')}>
            <SearchableSelect
              options={subjects.map(s => s.name)}
              value={subjects.find(s => s.id === formData.subjectId)?.name ?? ''}
              onChange={name => {
                const found = subjects.find(s => s.name === name);
                update('subjectId', found?.id ?? '');
                update('topicIds', []);
              }}
              placeholder="Select subject…"
              searchPlaceholder="Search subjects…"
            />
          </div>
          {fieldErrors.has('subjectId') && <p className="text-xs text-red-500 mt-1">Subject is required</p>}
        </FieldGroup>
        </div>

        <FieldGroup label="Topics" help={<FieldHint content={offeringWizardHelp} stepId="targeting" fieldKey="topicIds" />}>
          <SearchableMultiSelect
            options={topicsForSubject.map(t => t.name)}
            selected={topicsForSubject.filter(t => formData.topicIds.includes(t.id)).map(t => t.name)}
            onChange={names => {
              const ids = names.map(n => topicsForSubject.find(t => t.name === n)?.id).filter(Boolean) as string[];
              update('topicIds', ids);
            }}
            placeholder="Select topics…"
            searchPlaceholder="Search topics…"
          />
        </FieldGroup>

        <FieldGroup label="Target Exams" hint="optional" help={<FieldHint content={offeringWizardHelp} stepId="outcomes" fieldKey="targetExamId" />}>
          <SearchableMultiSelect
            options={exams.map(e => e.name)}
            selected={exams.filter(e => formData.targetExamIds.includes(e.id)).map(e => e.name)}
            onChange={names => {
              const ids = names.map(n => exams.find(e => e.name === n)?.id).filter(Boolean) as string[];
              update('targetExamIds', ids);
            }}
            placeholder="Select exams…"
            searchPlaceholder="Search exams…"
          />
        </FieldGroup>
      </div>

      {formData.offeringType !== 'CRASH_COURSE' && (
        <div className="space-y-2">
          <SectionLabel>Accepted Student Levels <FieldHint content={offeringWizardHelp} stepId="targeting" fieldKey="acceptedLevels" /></SectionLabel>
          {acceptedLevels.isError ? (
            <p role="alert" className="text-sm text-destructive">Couldn't load options. Retrying…</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {acceptedLevels.entries.map(l => (
                <button
                  key={l.code}
                  type="button"
                  disabled={acceptedLevels.isLoading}
                  onClick={() => toggleChip('acceptedLevels', l.code)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer select-none',
                    formData.acceptedLevels.includes(l.code)
                      ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                      : 'bg-background text-muted-foreground border-border hover:border-primary/40 hover:bg-muted/40',
                  )}
                >
                  {l.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-5">
      <p className="text-sm text-slate-500 -mt-1">Tell students what they'll gain and what they need to know upfront.</p>

      <FieldGroup label="What will students learn?" required help={<FieldHint content={offeringWizardHelp} stepId="outcomes" fieldKey="learningOutcomes" />}>
        <div className={cn((fieldErrors.has('learningOutcomes') || (showPlaceholderErrors && placeholderErrorFields.includes('Learning outcomes'))) && 'ring-2 ring-red-500 ring-offset-1 rounded-lg')}>
          <RichTextEditor
            value={formData.learningOutcomes}
            onChange={v => { update('learningOutcomes', v); setFieldErrors(prev => { const n = new Set(prev); n.delete('learningOutcomes'); return n; }); }}
            placeholder="By the end of this offering, students will be able to..."
            placeholderJump
          />
        </div>
        {fieldErrors.has('learningOutcomes') ? (
          <p className="text-xs text-red-500 mt-1">This field is required</p>
        ) : showPlaceholderErrors && placeholderErrorFields.includes('Learning outcomes') ? (
          <p className="text-xs text-red-500 mt-1">Replace the [bracketed] parts with your own words — use "Jump to next" above</p>
        ) : null}
      </FieldGroup>

      <FieldGroup label="Prerequisites" hint="optional" help={<FieldHint content={offeringWizardHelp} stepId="outcomes" fieldKey="prerequisites" />}>
        <div className={cn(showPlaceholderErrors && placeholderErrorFields.includes('Prerequisites') && 'ring-2 ring-red-500 ring-offset-1 rounded-lg')}>
          <RichTextEditor
            value={formData.prerequisites}
            onChange={v => update('prerequisites', v)}
            placeholderJump
            placeholder="Students should already know..."
          />
        </div>
        {showPlaceholderErrors && placeholderErrorFields.includes('Prerequisites') && (
          <p className="text-xs text-red-500 mt-1">Replace the [bracketed] parts with your own words — use "Jump to next" above</p>
        )}
      </FieldGroup>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldGroup label="Target Score Promise" hint="optional" help={<FieldHint content={offeringWizardHelp} stepId="outcomes" fieldKey="targetScorePromise" />}>
          <Input
            value={formData.targetScorePromise}
            onChange={e => update('targetScorePromise', e.target.value)}
            placeholder="e.g. Aim for 90%+ in CBSE boards"
            className={`h-10${formData.targetScorePromise.length > 500 ? ' border-red-500' : ''}`}
            maxLength={500}
          />
          {formData.targetScorePromise.length > 450 && (
            <p className={`text-xs mt-1 ${formData.targetScorePromise.length > 500 ? 'text-red-500' : 'text-slate-400'}`}>
              {formData.targetScorePromise.length}/500
            </p>
          )}
        </FieldGroup>
      </div>
    </div>
  );

  const renderAvailabilitySlots = () => (
    <div className="pt-2 border-t border-slate-100 space-y-3">
      <p className="text-sm text-slate-500">Add the days and time windows when you're generally available. Students use this to check fit.</p>
      <AvailabilitySlotsBuilder
        slots={formData.availabilitySlots}
        onChange={slots => {
          update('availabilitySlots', slots);
          const seen = new Set<string>();
          let hasDup = false;
          for (const sl of slots) {
            const key = `${sl.dayOfWeek}|${sl.timeFrom}|${sl.timeTo}`;
            if (seen.has(key)) { hasDup = true; break; }
            seen.add(key);
          }
          setFieldErrors(prev => {
            const next = new Set(prev);
            if (hasDup) next.add('availabilitySlots'); else next.delete('availabilitySlots');
            return next;
          });
        }}
      />
      {fieldErrors.has('availabilitySlots') && (
        <p className="text-xs text-red-500 mt-1">Duplicate availability slots are not allowed.</p>
      )}
    </div>
  );

  const renderStep5 = () => {
    if (formData.offeringType === 'ONE_TO_ONE') {
      return (
        <div className="space-y-5">
          <p className="text-sm text-slate-500 -mt-1">Set the default session duration for private lessons.</p>
          <FieldGroup label="Session Duration" required help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="sessionDurationMinutes" />}>
            <div className={cn('flex flex-wrap gap-2 pt-0.5', fieldErrors.has('oneToOneSessionDurationMinutes') && 'p-2 rounded-lg ring-2 ring-red-500 ring-offset-1')}>
              {DURATION_OPTIONS.map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => update('oneToOneSessionDurationMinutes', d)}
                  className={cn(
                    'px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all cursor-pointer select-none',
                    formData.oneToOneSessionDurationMinutes === d
                      ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                      : 'bg-background text-slate-600 border-border hover:border-primary/40',
                  )}
                >
                  {d} min
                </button>
              ))}
            </div>
            {fieldErrors.has('oneToOneSessionDurationMinutes') && <p className="text-xs text-red-500 mt-1">Please select a session duration</p>}
          </FieldGroup>
          {renderAvailabilitySlots()}
        </div>
      );
    }

    if (formData.offeringType === 'GROUP') {
      return (
        <div className="space-y-6">
          <p className="text-sm text-slate-500 -mt-1">Define batch size, schedule, and curriculum milestones.</p>

          <div>
            <SectionLabel>Batch Configuration <FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="batchSize" /></SectionLabel>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-4">
              <FieldGroup label="Min Batch Size" required help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="batchSizeMin" />}>
                <Input type="number" min={1} value={formData.groupBatchSizeMin} onChange={e => update('groupBatchSizeMin', e.target.value === '' ? '' : Number(e.target.value))} className={cn('h-10', fieldErrors.has('groupBatchSizeMin') && 'border-red-500')} />
                {fieldErrors.has('groupBatchSizeMin') && <p className="text-xs text-red-500 mt-1">Required</p>}
              </FieldGroup>
              <FieldGroup label="Max Batch Size" required help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="batchSizeMax" />}>
                <Input type="number" min={1} value={formData.groupBatchSizeMax} onChange={e => update('groupBatchSizeMax', e.target.value === '' ? '' : Number(e.target.value))} className={cn('h-10', fieldErrors.has('groupBatchSizeMax') && 'border-red-500')} />
                {fieldErrors.has('groupBatchSizeMax') && <p className="text-xs text-red-500 mt-1">Required</p>}
              </FieldGroup>
              <FieldGroup label="Sessions / Week" required help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="sessionsPerWeek" />}>
                <Input type="number" min={1} value={formData.groupSessionsPerWeek} onChange={e => update('groupSessionsPerWeek', e.target.value === '' ? '' : Number(e.target.value))} className={cn('h-10', fieldErrors.has('groupSessionsPerWeek') && 'border-red-500')} />
                {fieldErrors.has('groupSessionsPerWeek') && <p className="text-xs text-red-500 mt-1">Required</p>}
              </FieldGroup>
              <FieldGroup label="Session Duration" required help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="sessionDuration" />}>
                <select value={String(formData.groupSessionDurationMinutes)} onChange={e => update('groupSessionDurationMinutes', Number(e.target.value))} className={cn(selectCls, fieldErrors.has('groupSessionDurationMinutes') && 'border-red-500')}>
                  <option value="">Select…</option>
                  {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d} min</option>)}
                </select>
                {fieldErrors.has('groupSessionDurationMinutes') && <p className="text-xs text-red-500 mt-1">Required</p>}
              </FieldGroup>
              <FieldGroup label="Total Sessions" required help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="totalSessions" />}>
                <Input type="number" min={1} value={formData.groupTotalSessionsPlanned} onChange={e => update('groupTotalSessionsPlanned', e.target.value === '' ? '' : Number(e.target.value))} className={cn('h-10', fieldErrors.has('groupTotalSessionsPlanned') && 'border-red-500')} />
                {fieldErrors.has('groupTotalSessionsPlanned') && <p className="text-xs text-red-500 mt-1">Required</p>}
              </FieldGroup>
              <FieldGroup label="Next Batch Start" required help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="nextBatchStartDate" />}>
                <Input
                  id="group-next-batch-date"
                  type="date"
                  value={formData.groupNextBatchStartDate}
                  onChange={e => update('groupNextBatchStartDate', e.target.value)}
                  className={cn('h-10', (dupErrors?.nextBatchDate?.length || fieldErrors.has('groupNextBatchStartDate')) && 'border-red-500')}
                  aria-invalid={!!dupErrors?.nextBatchDate?.length}
                  aria-describedby={dupErrors?.nextBatchDate?.length ? 'group-next-batch-date-error' : undefined}
                />
                {dupErrors?.nextBatchDate && dupErrors.nextBatchDate.length > 0 ? (
                  <div role="alert" id="group-next-batch-date-error" className="mt-1 space-y-0.5">
                    {dupErrors.nextBatchDate.map((msg) => (
                      <p key={msg} className="text-xs text-red-500">{msg}</p>
                    ))}
                  </div>
                ) : (
                  fieldErrors.has('groupNextBatchStartDate') && <p className="text-xs text-red-500 mt-1">Required</p>
                )}
              </FieldGroup>
            </div>
          </div>

          <div>
            {renderAvailabilitySlots()}
          </div>
        </div>
      );
    }

    // CRASH_COURSE
    return (
      <div className="space-y-6">
        <p className="text-sm text-slate-500 -mt-1">Configure dates, sessions, seats, and the week-by-week curriculum plan.</p>

        <div>
          <SectionLabel>Course Schedule <FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="courseSchedule" /></SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <FieldGroup label="Start Date" required help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="courseStartDate" />}>
              <Input
                id="crash-course-start-date"
                type="date"
                value={formData.crashCourseStartDate}
                onChange={e => update('crashCourseStartDate', e.target.value)}
                className={cn('h-10', (dupErrors?.courseDates?.length || fieldErrors.has('crashCourseStartDate')) && 'border-red-500')}
                aria-invalid={!!dupErrors?.courseDates?.length}
                aria-describedby={dupErrors?.courseDates?.length ? 'crash-course-dates-error' : undefined}
              />
              {fieldErrors.has('crashCourseStartDate') && <p className="text-xs text-red-500 mt-1">Required</p>}
            </FieldGroup>
            <FieldGroup label="End Date" required help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="courseEndDate" />}>
              <Input
                type="date"
                value={formData.crashCourseEndDate}
                onChange={e => update('crashCourseEndDate', e.target.value)}
                className={cn('h-10', (dupErrors?.courseDates?.length || fieldErrors.has('crashCourseEndDate')) && 'border-red-500')}
                aria-invalid={!!dupErrors?.courseDates?.length}
                aria-describedby={dupErrors?.courseDates?.length ? 'crash-course-dates-error' : undefined}
              />
              {fieldErrors.has('crashCourseEndDate') && <p className="text-xs text-red-500 mt-1">Required</p>}
              {dupErrors?.courseDates && dupErrors.courseDates.length > 0 && (
                <div role="alert" id="crash-course-dates-error" className="mt-1 space-y-0.5">
                  {dupErrors.courseDates.map((msg) => (
                    <p key={msg} className="text-xs text-red-500">{msg}</p>
                  ))}
                </div>
              )}
            </FieldGroup>
            <FieldGroup label="Total Sessions" required help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="totalSessions" />}>
              <Input type="number" min={1} value={formData.crashCourseTotalSessions} onChange={e => update('crashCourseTotalSessions', e.target.value === '' ? '' : Number(e.target.value))} className={cn('h-10', fieldErrors.has('crashCourseTotalSessions') && 'border-red-500')} />
              {fieldErrors.has('crashCourseTotalSessions') && <p className="text-xs text-red-500 mt-1">Required</p>}
            </FieldGroup>
            <FieldGroup label="Sessions / Week" required help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="sessionsPerWeek" />}>
              <Input type="number" min={1} value={formData.crashCourseSessionsPerWeek} onChange={e => update('crashCourseSessionsPerWeek', e.target.value === '' ? '' : Number(e.target.value))} className={cn('h-10', fieldErrors.has('crashCourseSessionsPerWeek') && 'border-red-500')} />
              {fieldErrors.has('crashCourseSessionsPerWeek') && <p className="text-xs text-red-500 mt-1">Required</p>}
            </FieldGroup>
            <FieldGroup label="Session Duration" required help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="sessionDuration" />}>
              <select value={String(formData.crashCourseSessionDurationMinutes)} onChange={e => update('crashCourseSessionDurationMinutes', Number(e.target.value))} className={cn(selectCls, fieldErrors.has('crashCourseSessionDurationMinutes') && 'border-red-500')}>
                <option value="">Select…</option>
                {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d} min</option>)}
              </select>
              {fieldErrors.has('crashCourseSessionDurationMinutes') && <p className="text-xs text-red-500 mt-1">Required</p>}
            </FieldGroup>
            <FieldGroup label="Seats Total" required help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="seatsTotal" />}>
              <Input type="number" min={1} value={formData.crashCourseSeatsTotal} onChange={e => update('crashCourseSeatsTotal', e.target.value === '' ? '' : Number(e.target.value))} className={cn('h-10', fieldErrors.has('crashCourseSeatsTotal') && 'border-red-500')} />
              {fieldErrors.has('crashCourseSeatsTotal') && <p className="text-xs text-red-500 mt-1">Required</p>}
            </FieldGroup>
          </div>

          <div>
            {renderAvailabilitySlots()}
          </div>
        </div>

        <div>
          <SectionLabel>Requirements</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <FieldGroup label="Minimum Entry Level" required help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="minimumEntryLevel" />}>
              <select value={formData.crashCourseMinimumEntryLevel} onChange={e => update('crashCourseMinimumEntryLevel', e.target.value)} className={cn(selectCls, fieldErrors.has('crashCourseMinimumEntryLevel') && 'border-red-500')} disabled={acceptedLevels.isLoading}>
                <option value="">Select…</option>
                {acceptedLevels.entries.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
              {fieldErrors.has('crashCourseMinimumEntryLevel') && <p className="text-xs text-red-500 mt-1">Required</p>}
              {acceptedLevels.isError && <p role="alert" className="text-sm text-destructive mt-1">Couldn't load options. Retrying…</p>}
            </FieldGroup>
          </div>
        </div>

        <div>
          <SectionLabel>Extras</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <FieldGroup label="Mock Tests Included" help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="mockTestsIncluded" />}>
              <Input type="number" min={0} value={formData.crashCourseMockTestsIncluded} onChange={e => update('crashCourseMockTestsIncluded', e.target.value === '' ? '' : Number(e.target.value))} className="h-10" />
            </FieldGroup>
            <FieldGroup label="Doubt Clearing Sessions / Week" help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="doubtClearingSessionsPerWeek" />}>
              <Input type="number" min={0} value={formData.crashCourseDoubtClearingSessionsPerWeek} onChange={e => update('crashCourseDoubtClearingSessionsPerWeek', e.target.value === '' ? '' : Number(e.target.value))} className="h-10" />
            </FieldGroup>
          </div>
          <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-slate-50">
            <p className="text-sm font-semibold text-slate-700">Study Material Included? <FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="studyMaterialIncluded" /></p>
            <ToggleSwitch checked={formData.crashCourseStudyMaterial} onChange={() => update('crashCourseStudyMaterial', !formData.crashCourseStudyMaterial)} />
          </div>
        </div>

        <FieldGroup label="Previous Batch Results" hint="optional" help={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="previousBatchResults" />}>
          <Textarea value={formData.crashCoursePreviousBatchResults} onChange={e => update('crashCoursePreviousBatchResults', e.target.value)} placeholder="e.g. 45/50 students achieved 90%+ in JEE Mains 2025" rows={2} className="resize-none" />
        </FieldGroup>

      </div>
    );
  };

  const renderStepCurriculum = () => {
    if (formData.offeringType === 'ONE_TO_ONE') {
      return (
        <div className="space-y-4">
          <p className="text-sm text-slate-500 -mt-1">Curriculum planning applies to Group and Crash Course offerings.</p>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 flex flex-col items-center gap-3 text-center">
            <BookOpen className="w-8 h-8 text-slate-300" />
            <p className="text-sm font-medium text-slate-500">Not applicable for one-to-one sessions</p>
            <p className="text-xs text-slate-400 max-w-sm">One-to-one sessions are fully personalised — the curriculum adapts to each student in real time, so no fixed week-by-week plan is needed here.</p>
          </div>
        </div>
      );
    }

    if (formData.offeringType === 'GROUP') {
      return (
        <div className="space-y-4">
          <p className="text-sm text-slate-500 -mt-1">Plan the week-by-week curriculum for your group course. Students will see this on your offering page.</p>
          <MilestonesBuilder
            hint={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="milestones" />}
            milestones={formData.groupMilestones}
            onChange={m => {
              update('groupMilestones', m);
              if (m.length > 0) setFieldErrors(prev => { const next = new Set(prev); next.delete('groupMilestones'); return next; });
            }}
          />
          {fieldErrors.has('groupMilestones') && <p className="text-xs text-red-500 mt-2">At least one curriculum milestone is required</p>}
        </div>
      );
    }

    // CRASH_COURSE
    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-500 -mt-1">Plan the week-by-week curriculum for your crash course. Students will see this on your offering page.</p>
        <MilestonesBuilder
          hint={<FieldHint content={offeringWizardHelp} stepId="setup" fieldKey="weekPlan" />}
          milestones={formData.crashCourseMilestones}
          onChange={m => {
            update('crashCourseMilestones', m);
            if (m.length > 0) setFieldErrors(prev => { const next = new Set(prev); next.delete('crashCourseMilestones'); return next; });
          }}
        />
        {fieldErrors.has('crashCourseMilestones') && (
          <p className="text-xs text-red-500 mt-2">A week-by-week plan is required for crash courses</p>
        )}
      </div>
    );
  };

  const renderStep6 = () => (
    <div className="space-y-5">
      <p className="text-sm text-slate-500 -mt-1">
        Set your price per session in Indian Rupees (₹).
      </p>

      {/* Trial session toggle */}
      <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-slate-50">
        <div>
          <p className="text-sm font-semibold text-slate-700">Offer a trial session? <FieldHint content={offeringWizardHelp} stepId="schedule" fieldKey="trialSession" /></p>
          <p className="text-xs text-slate-500 mt-0.5">Students can try before buying. Set the trial price below.</p>
        </div>
        <ToggleSwitch
          checked={formData.trialSessionAvailable}
          onChange={() => update('trialSessionAvailable', !formData.trialSessionAvailable)}
        />
      </div>

      {/* Early bird deadline */}
      <FieldGroup label="Early Bird Deadline" hint="optional" help={<FieldHint content={offeringWizardHelp} stepId="schedule" fieldKey="earlyBird" />}>
        <p className="text-xs text-slate-500 -mt-1 mb-1.5">Set a deadline to unlock the early bird price below.</p>
        <Input
          type="date"
          value={formData.earlyBirdDeadline}
          onChange={e => update('earlyBirdDeadline', e.target.value)}
          className="h-10 max-w-[200px]"
        />
      </FieldGroup>

      <SectionLabel>Pricing Plans <FieldHint content={offeringWizardHelp} stepId="pricing" fieldKey="pricingPlans" /></SectionLabel>
      <PriceListEditor
        plans={formData.pricingPlans}
        onChange={plans => { update('pricingPlans', plans); setFieldErrors(prev => { const n = new Set(prev); n.delete('pricingPlans'); return n; }); }}
        showEarlyBirdPrice={!!formData.earlyBirdDeadline}
        showTrialPrice={formData.trialSessionAvailable}
      />
      {fieldErrors.has('pricingPlans') && (
        <p className="text-xs text-red-500">
          Enter a regular price to publish.
        </p>
      )}
    </div>
  );

  const renderStep7 = () => {
    const typeLabel = { ONE_TO_ONE: 'One-to-One', GROUP: 'Group', CRASH_COURSE: 'Crash Course' }[formData.offeringType as OfferingType] ?? formData.offeringType;
    const typeAccent: Record<string, string> = {
      ONE_TO_ONE: cn(OFFERING_TYPE_THEME.ONE_TO_ONE.pill, 'border', OFFERING_TYPE_THEME.ONE_TO_ONE.border),
      GROUP: cn(OFFERING_TYPE_THEME.GROUP.pill, 'border', OFFERING_TYPE_THEME.GROUP.border),
      CRASH_COURSE: cn(OFFERING_TYPE_THEME.CRASH_COURSE.pill, 'border', OFFERING_TYPE_THEME.CRASH_COURSE.border),
    };
    const subjectName = subjects.find(s => s.id === formData.subjectId)?.name ?? formData.subjectId;
    const topicNames = topicsForSubject.filter(t => formData.topicIds.includes(t.id)).map(t => t.name);
    const examNames = exams.filter(e => formData.targetExamIds.includes(e.id)).map(e => e.name);
    const plansFilled = formData.pricingPlans.filter(p => p.currency && p.regularPrice !== '');

    const Chips = ({ items, color }: { items: string[]; color: string }) => (
      <div className="flex flex-wrap gap-1.5">
        {items.map(i => (
          <span key={i} className={cn('text-xs px-2.5 py-1 rounded-full font-medium border', color)}>{i}</span>
        ))}
      </div>
    );

    // Section rows — bleed to outer card edges
    const Section = ({ label, children }: { label: string; children: React.ReactNode }) => (
      <div className="flex gap-4 px-5 sm:px-7 py-3.5 border-b border-slate-100 last:border-0">
        <span className="text-sm text-slate-400 w-36 shrink-0 pt-0.5 font-medium">{label}</span>
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    );

    const Val = ({ children }: { children: React.ReactNode }) => (
      <span className="text-sm font-semibold text-slate-900">{children}</span>
    );

    const modeChipColor: Record<string, string> = {
      ONLINE: 'bg-amber-50 text-amber-700 border-amber-200',
      OFFLINE: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      HYBRID: 'bg-violet-50 text-violet-700 border-violet-200',
    };

    const descriptionText = formData.description
      ? stripHtml(formData.description).trim()
      : null;

    const fmtTime = (t: string) => {
      const [hh, mm] = t.split(':').map(Number);
      const ampm = hh < 12 ? 'AM' : 'PM';
      const dh = hh % 12 === 0 ? 12 : hh % 12;
      return `${dh}:${String(mm).padStart(2, '0')} ${ampm}`;
    };

    const typeSetupRows = (() => {
      if (formData.offeringType === 'ONE_TO_ONE') {
        return [
          { label: 'Session Duration', value: formData.oneToOneSessionDurationMinutes ? `${formData.oneToOneSessionDurationMinutes} min` : null },
        ];
      }
      if (formData.offeringType === 'GROUP') {
        return [
          { label: 'Batch Size', value: formData.groupBatchSizeMin && formData.groupBatchSizeMax ? `${formData.groupBatchSizeMin}–${formData.groupBatchSizeMax} students` : null },
          { label: 'Sessions / Week', value: formData.groupSessionsPerWeek ? `${formData.groupSessionsPerWeek}` : null },
          { label: 'Session Duration', value: formData.groupSessionDurationMinutes ? `${formData.groupSessionDurationMinutes} min` : null },
          { label: 'Total Sessions', value: formData.groupTotalSessionsPlanned ? `${formData.groupTotalSessionsPlanned}` : null },
          { label: 'Next Batch Start', value: formData.groupNextBatchStartDate || null },
          { label: 'Milestones', value: formData.groupMilestones.length > 0 ? `${formData.groupMilestones.length} milestone(s)` : null },
        ];
      }
      if (formData.offeringType === 'CRASH_COURSE') {
        return [
          { label: 'Start Date', value: formData.crashCourseStartDate || null },
          { label: 'End Date', value: formData.crashCourseEndDate || null },
          { label: 'Total Sessions', value: formData.crashCourseTotalSessions ? `${formData.crashCourseTotalSessions}` : null },
          { label: 'Sessions / Week', value: formData.crashCourseSessionsPerWeek ? `${formData.crashCourseSessionsPerWeek}` : null },
          { label: 'Session Duration', value: formData.crashCourseSessionDurationMinutes ? `${formData.crashCourseSessionDurationMinutes} min` : null },
          { label: 'Total Seats', value: formData.crashCourseSeatsTotal ? `${formData.crashCourseSeatsTotal}` : null },
          { label: 'Min Entry Level', value: formData.crashCourseMinimumEntryLevel ? acceptedLevels.labelOf(formData.crashCourseMinimumEntryLevel) : null },
          { label: 'Study Material', value: formData.crashCourseStudyMaterial ? 'Included' : 'Not included' },
          { label: 'Mock Tests', value: formData.crashCourseMockTestsIncluded ? `${formData.crashCourseMockTestsIncluded}` : null },
          { label: 'Doubt Sessions / Wk', value: formData.crashCourseDoubtClearingSessionsPerWeek ? `${formData.crashCourseDoubtClearingSessionsPerWeek}` : null },
          { label: 'Milestones', value: formData.crashCourseMilestones.length > 0 ? `${formData.crashCourseMilestones.length} milestone(s)` : null },
        ];
      }
      return [];
    })();

    // Collect all visible type-setup rows
    const visibleTypeRows = typeSetupRows.filter(r => r.value);

    const leftoverPlaceholders = findLeftoverPlaceholders(formData);

    return (
      <div className="space-y-4">

        {showPlaceholderErrors && leftoverPlaceholders.length > 0 && (
          <div role="alert" className="rounded-xl bg-amber-50 px-4 py-3 flex gap-2.5">
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Some text still has [placeholders] to fill in</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Check: {leftoverPlaceholders.join(', ')}. Replace the [bracketed] parts with your own words before publishing.
              </p>
            </div>
          </div>
        )}

        {/* ── Hero ── */}
        <div className="rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-50 px-5 py-5">
          <p className="text-lg font-bold text-slate-900 leading-snug">
            {formData.title || <span className="text-slate-400 italic font-normal text-base">Untitled offering</span>}
          </p>
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <span className={cn('text-xs font-semibold px-2.5 py-1 rounded-full border', typeAccent[formData.offeringType] ?? 'bg-slate-100 text-slate-600 border-slate-200')}>
              {typeLabel}
            </span>
            {formData.teachingMode && (
              <span className={cn('text-xs font-semibold px-2.5 py-1 rounded-full border', modeChipColor[formData.teachingMode] ?? 'bg-slate-100 text-slate-600 border-slate-200')}>
                {teachingModes.labelOf(formData.teachingMode)}
              </span>
            )}
            {subjectName && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full border bg-orange-50 text-orange-700 border-orange-200">
                {subjectName}
              </span>
            )}
            {plansFilled.length > 0 && (() => {
              const sym = currencySymbol(plansFilled[0].currency as string);
              return (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                  from {sym}{plansFilled[0].regularPrice}
                </span>
              );
            })()}
          </div>
          {descriptionText && (
            <p className="text-sm text-slate-500 mt-2.5 leading-relaxed">{descriptionText}</p>
          )}
        </div>

        {/* ── Summary rows — bleed to outer card edges ── */}
        <div className="-mx-5 sm:-mx-7 border-t border-slate-100 divide-y divide-slate-100">

          {/* Basics */}
          {formData.category && (
            <Section label="Category">
              <Val>{formData.category.replace(/_/g, ' ')}</Val>
            </Section>
          )}

          {/* Subject + Topics */}
          {subjectName && (
            <Section label="Subject">
              <Val>{subjectName}</Val>
              {topicNames.length > 0 && (
                <div className="mt-2">
                  <Chips items={topicNames} color="bg-orange-50 text-orange-700 border-orange-200" />
                </div>
              )}
            </Section>
          )}

          {/* Grade Bands */}
          {formData.gradeBands.length > 0 && (
            <Section label="Grade Bands">
              <Chips items={formData.gradeBands.map(gradeBands.labelOf)} color="bg-indigo-50 text-indigo-700 border-indigo-200" />
            </Section>
          )}

          {/* Curriculum */}
          {formData.curriculumSystems.length > 0 && (
            <Section label="Curriculum">
              <Chips items={formData.curriculumSystems.map(curricula.labelOf)} color="bg-sky-50 text-sky-700 border-sky-200" />
            </Section>
          )}

          {/* Target Exams */}
          {examNames.length > 0 && (
            <Section label="Target Exams">
              <Chips items={examNames} color="bg-rose-50 text-rose-700 border-rose-200" />
            </Section>
          )}

          {/* Outcomes */}
          {formData.learningOutcomes && (
            <Section label="Learning Outcomes">
              <div className="text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: formData.learningOutcomes }} />
            </Section>
          )}
          {formData.prerequisites && (
            <Section label="Prerequisites">
              <div className="text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: formData.prerequisites }} />
            </Section>
          )}
          {formData.targetScorePromise && (
            <Section label="Score Promise">
              <Val>{formData.targetScorePromise}</Val>
            </Section>
          )}

          {/* Type-specific rows */}
          {visibleTypeRows.map(r => (
            <Section key={r.label} label={r.label}>
              <Val>{r.value}</Val>
            </Section>
          ))}

          {/* Availability */}
          {formData.availabilitySlots.length > 0 && (
            <Section label="Availability">
              <div className="flex flex-wrap gap-1.5">
                {formData.availabilitySlots.map((sl, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-full border bg-violet-50 text-violet-700 border-violet-200 font-medium">
                    {sl.dayOfWeek} {fmtTime(sl.timeFrom)}–{fmtTime(sl.timeTo)}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {/* Trial / Early bird */}
          {formData.trialSessionAvailable && (
            <Section label="Trial Session">
              <Val>Enabled</Val>
            </Section>
          )}
          {formData.earlyBirdDeadline && (
            <Section label="Early Bird Deadline">
              <Val>{formData.earlyBirdDeadline}</Val>
            </Section>
          )}

          {/* Pricing */}
          <Section label="Pricing">
            {plansFilled.length === 0 ? (
              <div className="flex items-center gap-1.5 text-sm text-amber-700">
                <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                No prices added — required to publish
              </div>
            ) : (
              <div className="space-y-2">
                {plansFilled.map((p) => {
                  const sym = currencySymbol(p.currency as string);
                  return (
                    <div key={p.currency as string} className="flex flex-wrap items-center gap-3">
                      <span className="text-sm font-bold text-slate-900">{sym}{p.regularPrice}</span>
                      {p.earlyBirdPrice !== undefined && p.earlyBirdPrice !== '' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">
                          Early bird {sym}{p.earlyBirdPrice}
                        </span>
                      )}
                      {p.trialSessionPrice !== undefined && p.trialSessionPrice !== '' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 font-medium">
                          Trial {sym}{p.trialSessionPrice}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

        </div>

        {/* Profile warning */}
        {!isProfileApproved && (
          <div className="rounded-xl bg-amber-50 p-4 flex items-start gap-3 mt-2">
            <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">Your profile isn't approved yet. You can create and publish this offering, but it won't show in student search until your profile is approved.</p>
          </div>
        )}

        {/* F15 — subscription-tier publish restriction (known ahead of time via
            /entitlements, or discovered from a 409 on the last publish attempt) */}
        {tierBenefitsOn && effectivePublishBlock && (
          <PublishRestrictionNotice
            reason={effectivePublishBlock}
            offeringType={formData.offeringType}
            className="mt-2"
          />
        )}
      </div>
    );
  };

  // Targeting first, then Details (no Type step — type is selected via the top picker)
  const stepRenderers = [
    renderStep2, renderStep1, renderStep3,
    renderStep5, renderStepCurriculum, renderStep6,
  ];

  const canGoNext = step < 5;
  const canGoBack = step > 0;

  // Step indicator — circular icons + connectors, same pattern as TeacherProfileSetup
  const renderStepIndicator = () => {
    return (
      <div className="flex items-start w-full">
        {WIZARD_STEPS.map((s, idx) => {
          const isActive = idx === step;
          // Steps are freely clickable once an offering type is chosen
          const isClickable = !isActive && !!formData.offeringType;
          const isComplete = computeStepErrors(idx).size === 0;
          // Setup step icon (idx 3) depends on offering type
          const StepIcon = idx === 3
            ? getOfferingTypeTheme(formData.offeringType).icon
            : s.icon;
          return (
            <div key={s.id} className="flex items-start flex-1 min-w-0">
              <div className="flex flex-col items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => isClickable && setStep(idx)}
                  aria-label={`Go to ${s.label} step`}
                  className={cn(
                    'w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all duration-200',
                    // Complete (not current) — emerald green: universally understood as "done"
                    isComplete && !isActive && 'bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600 cursor-pointer',
                    // Active + already complete — brand indigo with glow ring so current step stands out
                    isActive && isComplete  && 'bg-primary border-primary text-primary-foreground shadow-[0_0_0_4px_hsl(var(--primary)/0.18)] cursor-default',
                    // Active + not yet complete — white fill, indigo border + glow ring
                    isActive && !isComplete && 'bg-background border-primary text-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.14)] cursor-default',
                    // Upcoming — neutral
                    !isComplete && !isActive && (isClickable
                      ? 'bg-background border-border text-muted-foreground hover:border-primary/50 hover:text-primary cursor-pointer'
                      : 'bg-background border-border text-muted-foreground cursor-default'),
                  )}
                >
                  {isComplete && !isActive
                    ? <Check className="w-4 h-4" />
                    : <StepIcon className="w-4 h-4" />
                  }
                </button>
                <div className="hidden sm:flex flex-col items-center">
                  <span className={cn(
                    'text-xs font-semibold text-center leading-tight max-w-[72px] truncate',
                    isActive ? 'text-primary' : isComplete ? 'text-foreground' : 'text-muted-foreground',
                  )}>
                    {s.label}
                  </span>
                  {s.optional && !isComplete && !isActive && (
                    <span className="text-xs text-muted-foreground/60 font-medium mt-0.5">Optional</span>
                  )}
                </div>
              </div>
              {idx < WIZARD_STEPS.length - 1 && (
                <div className={cn(
                  'flex-1 h-0.5 mt-[18px] mx-1 transition-colors duration-300',
                  isComplete ? 'bg-emerald-500' : 'bg-border',
                )} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const offeringTypeLabel = formData.offeringType
    ? ({ ONE_TO_ONE: 'One-to-One', GROUP: 'Group', CRASH_COURSE: 'Crash Course' }[formData.offeringType] ?? '')
    : null;

  return (
    <div className="min-h-screen bg-white teacher-theme">
      <SidebarProvider>
        <DashboardTopNav role="teacher" />
        <div className="min-h-screen flex w-full pt-14">
          <TeacherSidebar teacherName={teacherName} teacherAvatar={teacherAvatar} subjects={teacherSubjects} />

          <div className="flex-1 flex flex-col min-w-0">
            <main className="flex-1 overflow-auto bg-slate-50">
              <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-7xl">

                {/* ── Persistent offering-type picker ── */}
                <div className="mb-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2.5">
                    Offering type
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {OFFERING_TYPE_OPTIONS.map(({ type, label, desc }) => {
                      const theme = OFFERING_TYPE_THEME[type];
                      const Icon = theme.icon;
                      const isSelected = formData.offeringType === type;
                      return (
                        <button
                          key={type}
                          type="button"
                          disabled={isEditingMode}
                          onClick={() => {
                            if (isEditingMode || formData.offeringType === type) return;
                            update('offeringType', type);
                          }}
                          className={cn(
                            'relative text-left rounded-xl p-4 border-2 transition-all',
                            isEditingMode ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:shadow-sm',
                            isSelected
                              ? cn('shadow-sm', theme.soft, theme.border)
                              : 'border-slate-200 bg-white hover:border-slate-300',
                          )}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Icon className={cn('w-4 h-4 shrink-0', isSelected ? theme.accent : 'text-slate-400')} />
                            <p className={cn('font-semibold text-sm leading-tight', isSelected ? theme.accent : 'text-slate-700')}>{label}</p>
                          </div>
                          <p className="text-xs text-slate-400 leading-relaxed">{desc}</p>
                          {isSelected && (
                            <span className={cn('absolute top-2.5 right-2.5 w-5 h-5 rounded-full flex items-center justify-center', theme.accent.replace('text-', 'bg-'))}>
                              <Check className="w-3 h-3 text-white" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {isEditingMode && (
                    <p className="text-xs text-slate-400 flex items-center gap-1.5 mt-2">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      Offering type cannot be changed after creation. Create a new offering to use a different type.
                    </p>
                  )}
                  {renderCloneSection()}
                </div>

                {/* ── Step indicator ── */}
                <div className="mb-6">
                  {renderStepIndicator()}
                </div>

                {/* Step content (+ contextual help at xl) */}
                <div className="flex items-start gap-6">
                <div className="flex-1 min-w-0 bg-white rounded-2xl shadow-md shadow-slate-200/60">
                  {/* Content */}
                  <div className="p-6 sm:p-8 lg:p-10">
                    {showPlaceholderErrors && placeholderErrorFields.length > 0 && (
                      <div role="alert" className="rounded-xl bg-red-50 px-4 py-3 flex gap-2.5 mb-5">
                        <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-red-800">Almost there — replace the [placeholders] first</p>
                          <p className="text-xs text-red-700 mt-0.5">
                            Your offering can't be published while template [placeholders] are left in:{' '}
                            <span className="font-semibold">{placeholderErrorFields.join(', ')}</span>.
                            The fields are highlighted below{placeholderErrorFields.some(f => f === 'Learning outcomes' || f === 'Prerequisites') && (step === 2) ? ' and on the Outcomes step' : ''}.
                          </p>
                        </div>
                      </div>
                    )}
                    {stepRenderers[step]()}
                  </div>

                  {/* Footer: Previous / Next / Publish */}
                  <div className="px-6 sm:px-8 lg:px-10 pb-6 sm:pb-7 pt-4 border-t border-slate-100">
                    <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-2">
                      {/* Left: Previous */}
                      {canGoBack && !(id && step === 1) ? (
                        <Button
                          variant="outline"
                          className="gap-2 sm:w-auto w-full"
                          onClick={() => {
                            setFieldErrors(new Set());
                            if (step === 0 && normalizedTypeParam) {
                              navigate('/create-offering');
                            } else {
                              setStep(s => Math.max(0, s - 1));
                            }
                          }}
                        >
                          <ChevronLeft className="w-4 h-4" /> Previous
                        </Button>
                      ) : (
                        <div />
                      )}

                      {/* Right: Save as Draft (every step) + Next (steps 0–6) or Publish (step 7) */}
                      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:ml-auto items-stretch sm:items-center gap-2">
                        <Button
                          variant="outline"
                          className="gap-2 sm:w-auto w-full cursor-pointer"
                          onClick={handleSaveDraft}
                          disabled={saving || publishing || !formData.offeringType || !formData.title.trim()}
                        >
                          {saving ? 'Saving…' : 'Save as Draft'}
                        </Button>
                        {step >= 0 && step <= 3 && formData.offeringType && (
                          <Button
                            variant="ghost"
                            className="gap-1.5 sm:w-auto w-full text-slate-500 hover:text-primary text-xs"
                            onClick={() => setStep(5)}
                            title="Jump straight to pricing — you can fill in the other steps later"
                          >
                            Skip to Pricing <ChevronsRight className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {showCloneCancelButton && (
                          <Button
                            variant="outline"
                            className="gap-2 sm:w-auto w-full text-destructive border-destructive/30 hover:bg-destructive/5"
                            onClick={() => setShowCloneCancelConfirm(true)}
                          >
                            Cancel
                          </Button>
                        )}
                        {canGoNext && (
                          <Button
                            className="gap-2 sm:w-auto w-full"
                            disabled={!formData.offeringType}
                            onClick={() => {
                              if (!validateStep(step)) return;
                              if (step === 1 && placeholderErrorFields.length > 0) {
                                toast.warning(
                                  `${placeholderErrorFields.length === 1 ? '1 field still has' : `${placeholderErrorFields.length} fields still have`} [bracketed] placeholders — come back and fill them in before publishing.`,
                                  { duration: 5000 },
                                );
                              }
                              setStep(s => Math.min(5, s + 1));
                            }}
                          >
                            Next <ChevronRight className="w-4 h-4" />
                          </Button>
                        )}
                        {!canGoNext && formData.offeringType && (
                          <Button
                            className="xl:hidden gap-2 sm:w-auto w-full cursor-pointer"
                            onClick={handlePublish}
                            disabled={
                              publishing || saving
                              || formData.pricingPlans.filter(p => p.currency && p.regularPrice !== '').length === 0
                              || Boolean(tierBenefitsOn && effectivePublishBlock)
                            }
                            title={tierBenefitsOn && effectivePublishBlock ? 'Blocked by your current subscription plan — see the notice above' : undefined}
                          >
                            <CheckCircle className="w-4 h-4" />
                            {publishing ? 'Publishing…' : 'Publish Offering'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {!!formData.offeringType && (
                  <div className="hidden xl:flex flex-col gap-3 w-80 shrink-0 sticky top-24">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Listing preview</p>
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                      {/* Title + type badge */}
                      <div className={cn('px-4 py-3 border-b border-slate-100', offeringTypeLabel && formData.offeringType in OFFERING_TYPE_THEME ? getOfferingTypeTheme(formData.offeringType).soft : 'bg-slate-50')}>
                        <p className="text-sm font-bold text-slate-900 leading-snug line-clamp-2">
                          {formData.title || <span className="text-slate-400 font-normal italic">No title yet</span>}
                        </p>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {offeringTypeLabel && (
                            <span className={cn('text-xs font-semibold px-2 py-0.5 rounded-full border', formData.offeringType in OFFERING_TYPE_THEME ? cn(getOfferingTypeTheme(formData.offeringType).pill, getOfferingTypeTheme(formData.offeringType).border) : 'bg-slate-100 text-slate-600 border-slate-200')}>
                              {offeringTypeLabel}
                            </span>
                          )}
                          {formData.teachingMode && (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                              {teachingModes.labelOf(formData.teachingMode)}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Key details */}
                      <div className="divide-y divide-slate-100 text-xs">
                        {(() => {
                          const subjectName = subjects.find(s => s.id === formData.subjectId)?.name;
                          const plansFilled = formData.pricingPlans.filter(p => p.currency && p.regularPrice !== '');
                          const rows = [
                            subjectName && { label: 'Subject', value: subjectName },
                            formData.gradeBands.length > 0 && { label: 'Grades', value: formData.gradeBands.map(gradeBands.labelOf).join(', ') },
                            formData.curriculumSystems.length > 0 && { label: 'Curriculum', value: formData.curriculumSystems.map(curricula.labelOf).join(', ') },
                            plansFilled.length > 0 && { label: 'Price from', value: `${currencySymbol(plansFilled[0].currency as string)}${plansFilled[0].regularPrice}` },
                          ].filter(Boolean) as { label: string; value: string }[];
                          return rows.map(r => (
                            <div key={r.label} className="flex gap-2 px-4 py-2">
                              <span className="text-slate-400 font-medium w-24 shrink-0">{r.label}</span>
                              <span className="text-slate-700 font-semibold line-clamp-2 break-words min-w-0" title={r.value}>{r.value}</span>
                            </div>
                          ));
                        })()}
                        {formData.pricingPlans.filter(p => p.currency && p.regularPrice !== '').length === 0 && (
                          <div className="px-4 py-2 text-slate-400 italic">No price set yet</div>
                        )}
                      </div>
                      {/* Completion checklist — driven by computeStepErrors so it matches actual validation */}
                      <div className="border-t border-slate-100 px-4 py-3 space-y-1.5">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Checklist</p>
                        {([
                          { label: 'Audience',             done: computeStepErrors(0).size === 0 },
                          { label: 'Title & description',  done: computeStepErrors(1).size === 0 },
                          { label: 'Learning outcomes',    done: computeStepErrors(2).size === 0 },
                          { label: 'Schedule',
                            done: computeStepErrors(3).size === 0 },
                          { label: 'Curriculum',           done: computeStepErrors(4).size === 0 },
                          { label: 'Pricing',              done: computeStepErrors(5).size === 0 },
                        ] as { label: string; done: boolean }[]).map(item => (
                          <div key={item.label} className="flex items-center gap-2">
                            <span className={cn('w-4 h-4 rounded-full flex items-center justify-center shrink-0', item.done ? 'bg-emerald-500' : 'bg-slate-200')}>
                              {item.done && <Check className="w-2.5 h-2.5 text-white" />}
                            </span>
                            <span className={cn('text-xs', item.done ? 'text-slate-700 font-medium' : 'text-slate-400')}>{item.label}</span>
                          </div>
                        ))}
                      </div>
                      {/* Publish button */}
                      <div className="border-t border-slate-100 px-4 py-3">
                        <Button
                          className="w-full gap-2 cursor-pointer"
                          onClick={handlePublish}
                          disabled={
                            publishing || saving
                            || formData.pricingPlans.filter(p => p.currency && p.regularPrice !== '').length === 0
                            || Boolean(tierBenefitsOn && effectivePublishBlock)
                          }
                          title={tierBenefitsOn && effectivePublishBlock ? 'Blocked by your current subscription plan — see the notice above' : undefined}
                        >
                          <CheckCircle className="w-4 h-4" />
                          {publishing ? 'Publishing…' : 'Publish Offering'}
                        </Button>
                        {formData.pricingPlans.filter(p => p.currency && p.regularPrice !== '').length === 0 && (
                          <p className="text-xs text-slate-400 text-center mt-2">Add pricing to enable publish</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                </div>

              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>

      <AlertDialog open={showCloneCancelConfirm} onOpenChange={setShowCloneCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this offering?</AlertDialogTitle>
            <AlertDialogDescription>
              This looks like a duplicate of an offering you already have. Exiting now
              will discard everything entered so far — nothing will be saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => navigate('/teacher-offerings')}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
